import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Amplify } from "aws-amplify";
import { signOut, getCurrentUser, fetchAuthSession } from "aws-amplify/auth";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigation } from "../hooks/useQueryCustom";
import { silentAuth } from "../utils/oauth";

const USERPOOL_ID = import.meta.env.VITE_USERPOOL_ID;
const USERPOOL_CLIENT_ID = import.meta.env.VITE_USERPOOL_CLIENT_ID;
const COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN;
const COGNITO_REDIRECT = import.meta.env.VITE_COGNITO_REDIRECT;

const UserContext = createContext({
  userProfile: undefined,
  isLogged: false,
  isAmico: false,
  isAdmin: false,
  login: () => 0,
  logout: () => 0,
  callApi: async (url = "", options = {}) => new Response({}),
});

export const getUserData = async (forceRefresh) => {
  const loggedUser = await getCurrentUser();
  const userSession = await fetchAuthSession(
    forceRefresh ? { forceRefresh: true } : undefined,
  );
  return {
    ...loggedUser,
    attributes: userSession.tokens?.idToken?.payload,
    signInUserSession: userSession.tokens,
  };
};

export const getUserEmail = async (session) => {
  const user = await getCurrentUser();
  return (
    session.tokens?.idToken?.payload?.email ||
    session.tokens?.idToken?.payload?.identities?.[0]?.userId ||
    user.signInDetails?.loginId ||
    "user"
  );
};

export const UserProvider = ({ children }) => {
  const [userProfile, setUserProfile] = useState();
  const [isLogged, setIsLogged] = useState();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  //console.log("user islogged: ", isLogged);

  const isAdmin =
    !!userProfile?.signInUserSession?.idToken?.payload[
      "cognito:groups"
    ]?.includes("amministratori");
  const isAmico =
    !!userProfile?.signInUserSession?.idToken?.payload[
      "cognito:groups"
    ]?.includes("amici");

  const { data: navigationData } = useNavigation(isAdmin);

  const logout = useCallback(async () => {
    try {
      await signOut({
        global: true,
      });
      setUserProfile();
      const areaClientiLink =
        (
          (navigationData?.pages ?? [])?.find(
            (page) => page.template === "areaClienti",
          ) || null
        )?.link || "#";
      navigate(areaClientiLink || "/");
      setIsLogged(false);
    } catch (err) {
      console.error("Logout err: ", err);
    }
  }, [navigate, navigationData]);

  const login = useCallback(async (user) => {
    setUserProfile(user);
    setIsLogged(true);
  }, []);

  useEffect(() => {
    if (
      userProfile &&
      !userProfile.attributes?.["custom:cap"] &&
      !["/profilo", "/profilo/"].includes(location.pathname)
    ) {
      navigate("/profilo");
    }
  }, [userProfile, location, navigate]);

  useEffect(() => {
    const loadUser = async (forceRefresh = false) => {
      //console.log("loaduser");
      try {
        const userData = await getUserData(forceRefresh);
        login(userData);
        console.log("User loaded: ", userData);
        return true;
      } catch (err) {
        console.warn("Check user session", err);
        setIsLogged(false);
        return false;
      }
    };
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: USERPOOL_ID,
          userPoolClientId: USERPOOL_CLIENT_ID,
          loginWith: COGNITO_DOMAIN
            ? {
                oauth: {
                  domain: COGNITO_DOMAIN,
                  redirectSignIn: COGNITO_REDIRECT
                    ? [COGNITO_REDIRECT]
                    : [window.location.origin],
                  redirectSignOut: COGNITO_REDIRECT
                    ? [COGNITO_REDIRECT]
                    : [window.location.origin],
                  responseType: "code",
                  scopes: [
                    "openid",
                    "aws.cognito.signin.user.admin",
                    "email",
                    "profile",
                  ],
                },
              }
            : undefined,
        },
      },
    });

    const handler = async (event) => {
      if (event.origin !== window.location.origin) {
        console.log("Ignoring message from unknown origin: ", event.origin);
        return;
      }

      const data = event.data;

      if (!data || data.type !== "COGNITO_SILENT_AUTH") {
        return;
      }

      if (data.status === "anonymous") {
        console.log("Silent auth failed, user is anonymous");
        setIsLogged(false);
        document.querySelector("#silent-auth-iframe")?.remove();
        return;
      }

      if (data.status !== "authenticated") {
        return;
      }

      console.log("Silent auth success");
      document.querySelector("#silent-auth-iframe")?.remove();
      loadUser(true).then((logged) => {
        if (logged) {
          console.log("Silent auth success, user loaded");
        } else {
          console.error("Silent auth failed to load user");
        }
      });
    };

    let timeoutId;
    loadUser().then((logged) => {
      if (!logged) {
        window.addEventListener("message", handler);
        silentAuth().catch((err) => {
          console.error("Silent auth error: ", err);
        });
        timeoutId = setTimeout(() => {
          document.querySelector("#silent-auth-iframe")?.remove();
          window.removeEventListener("message", handler);
        }, 20000);
      }
    });

    return () => {
      window.removeEventListener("message", handler);
      clearTimeout(timeoutId);
    };
  }, [login]);

  useEffect(() => {
    const pageShowListener = (event) => {
      if (event.persisted) {
        // The page was restored from the bfcache
        // Invalidate cart data
        console.log("Clear cart query from BFCACHE");
        queryClient.removeQueries(["cart", userProfile?.username]);
      }
    };
    window.addEventListener("pageshow", pageShowListener);
    return () => window.removeEventListener("pageshow", pageShowListener);
  }, [queryClient, userProfile]);

  const callApi = useCallback(
    async (path, options = {}) => {
      const { headers, ...otherOptions } = options;
      console.log("callApi");
      const { tokens } = await fetchAuthSession();
      const accessToken = tokens?.idToken?.toString();
      if (!accessToken) {
        await logout();
        throw new Error("No access token available");
      }
      const response = await fetch(path, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          ...headers,
        },
        ...otherOptions,
      });
      if (response.ok) {
        if (response.headers.get("Content-Type") === "application/pdf") {
          return await response.blob();
        }
        return await response.json();
      } else {
        if (response.status === 401) {
          console.error("Authentication error");
          await logout();
        }
        const data = await response.json();
        const error = new Error(
          `Error calling ${path} ${response.status} ${response.statusText}`,
        );
        error.status = response.status;
        error.desc = data?.error;
        error.code = data?.code;
        throw error;
      }
    },
    [logout],
  );

  const contextValue = useMemo(
    () => ({
      isLogged,
      isAmico,
      isAdmin,
      userProfile,
      login,
      logout,
      callApi,
    }),
    [isLogged, isAmico, isAdmin, userProfile, login, logout, callApi],
  );
  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  );
};

export default UserContext;
